import { createContext, ReactNode, useContext, useState } from 'react';

import {
  DefectStatus,
  ReportState,
} from 'business/quality-control/pages/record-segment-defect/components/left-panel/board-actions/types';
import { Stroke } from 'business/quality-control/pages/record-segment-defect/hooks/use-drawing';
import usePersistedStringState from 'technical/persist';
import {
  BoardViewMode,
  DEFAULT_DRAW_COLOR,
} from 'ui/quality-control/board/utils';

type BoardContextType = {
  isDrawingEnable: boolean;
  drawingColor: string;
  segmentKey: string | null;
  defectStroke: Stroke | null;
  defectFilter: DefectStatus;
  defectReportStateFilter: ReportState;
  repairReportStateFilter: ReportState;
  boardViewMode: BoardViewMode;
  defectId: string | null;
  strokeResetKey: number;
  boardConstructionSiteId: string | undefined;
  setIsDrawingEnable: (value: boolean) => void;
  startDrawing: (value: string) => void;
  stopDrawing: () => void;
  pauseDrawing: () => void;
  setSegmentKey: (value: string | null) => void;
  setDefectStroke: (value: Stroke | null) => void;
  setDefectFilter: (value: DefectStatus) => void;
  setDefectReportStateFilter: (value: ReportState) => void;
  setRepairReportStateFilter: (value: ReportState) => void;
  setBoardViewMode: (value: BoardViewMode) => void;
  setDrawingColor: (value: string) => void;
  setDefectId: (value: string | null) => void;
  setBoardConstructionSiteId: (value: string | undefined) => void;
};

const BoardContext = createContext<BoardContextType | null>(null);

export const BoardContextProvider = ({ children }: { children: ReactNode }) => {
  const [isDrawingEnable, setIsDrawingEnable] = useState(false);
  const [drawingColor, setDrawingColor] = useState<string>(DEFAULT_DRAW_COLOR);
  const [segmentKey, setSegmentKey] = useState<string | null>(null);
  const [defectStroke, setDefectStroke] = useState<Stroke | null>(null);
  const [defectFilter, setDefectFilter] = useState<DefectStatus>(
    DefectStatus.All,
  );
  const [defectReportStateFilter, setDefectReportStateFilter] =
    useState<ReportState>(ReportState.All);
  const [repairReportStateFilter, setRepairReportStateFilter] =
    useState<ReportState>(ReportState.All);
  const [boardViewMode, setBoardViewMode] = useState<BoardViewMode>(
    BoardViewMode.Entry,
  );
  const [defectId, setDefectId] = useState<string | null>(null);
  const [strokeResetKey, setStrokeResetKey] = useState(0);
  const [boardConstructionSiteId, setBoardConstructionSiteId] =
    usePersistedStringState('recordSegmentDefectBoardConstructionSiteId');

  const startDrawing = (color: string) => {
    setDrawingColor(color);
    setIsDrawingEnable(true);
  };

  const stopDrawing = () => {
    setIsDrawingEnable(false);
    setSegmentKey(null);
    setDefectStroke(null);
    setStrokeResetKey((k) => k + 1);
  };

  const pauseDrawing = () => {
    setIsDrawingEnable(false);
    setSegmentKey(null);
  };

  return (
    <BoardContext.Provider
      value={{
        isDrawingEnable,
        drawingColor,
        segmentKey,
        defectStroke,
        defectFilter,
        defectReportStateFilter,
        repairReportStateFilter,
        boardViewMode,
        defectId,
        strokeResetKey,
        boardConstructionSiteId,
        setIsDrawingEnable,
        startDrawing,
        stopDrawing,
        pauseDrawing,
        setSegmentKey,
        setDefectStroke,
        setDefectFilter,
        setDefectReportStateFilter,
        setRepairReportStateFilter,
        setBoardViewMode,
        setDrawingColor,
        setDefectId,
        setBoardConstructionSiteId,
      }}
    >
      {children}
    </BoardContext.Provider>
  );
};

export const useBoardContext = () => {
  const ctx = useContext(BoardContext);
  if (!ctx) {
    throw new Error('useBoardContext must be used within BoardContextProvider');
  }
  return ctx;
};
